import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2, Play, Power, CalendarClock } from "lucide-react";
import { useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, Badge, RUN_STYLE, fmtDate } from "./shared";

export default function SyncJobsPage() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setItems([]); return; }
    setLoading(true);
    integrationService.syncJobs(params)
      .then((d) => setItems(d.items || []))
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params]);
  useEffect(() => { load(); }, [load]);

  const run = async (j) => {
    setBusy(j.id);
    try { const r = await integrationService.runJob(j.id, params); toast.success(r.summary); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const toggle = async (j) => {
    try { await (j.enabled ? integrationService.disableJob : integrationService.enableJob)(j.id, params); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const suffix = tenantId ? `?tenantId=${tenantId}` : "";

  return (
    <div data-testid="sync-jobs-page">
      <PageHeader icon={RefreshCw} title="Sync Jobs" subtitle="Scheduled or manual jobs that map source data into canonical entities.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="jobs-empty"><p className="text-sm text-muted-foreground">No sync jobs. Create one from an integration.</p></div>
      ) : (
        <div className="space-y-2" data-testid="jobs-list">
          {items.map((j) => (
            <div key={j.id} className="flex items-center gap-3 bg-card border border-border rounded-xl p-4 flex-wrap" data-testid={`job-row-${j.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground">{j.name} <span className="text-[11px] text-muted-foreground font-normal">→ {j.targetEntity}</span></p>
                <p className="text-[11px] text-muted-foreground flex items-center gap-2">
                  <CalendarClock className="h-3 w-3" /> {j.schedule?.enabled ? `Scheduled · ${j.schedule.frequency}` : "Manual"} · last run {fmtDate(j.lastRunAt)}
                  {j.lastRunStatus && <Badge label={j.lastRunStatus} styleMap={RUN_STYLE} />}
                </p>
              </div>
              <button onClick={() => toggle(j)} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${j.enabled ? "text-emerald-600 bg-emerald-500/10 border-emerald-500/30" : "text-muted-foreground bg-muted/40 border-border"}`} data-testid={`jobs-toggle-${j.id}`}>
                <Power className="h-3 w-3 inline mr-0.5" />{j.enabled ? "Enabled" : "Disabled"}
              </button>
              <button onClick={() => run(j)} disabled={busy === j.id} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid={`jobs-run-${j.id}`}>
                {busy === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run
              </button>
              <Link to={`/admin/integrations/sync-logs?jobId=${j.id}${tenantId ? `&tenantId=${tenantId}` : ""}`} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid={`jobs-logs-${j.id}`}>Logs</Link>
              <Link to={`/admin/integrations/${j.integrationId}${suffix}`} className="text-[11px] text-primary hover:underline" data-testid={`jobs-integration-${j.id}`}>Open</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
